/* eslint-disable prettier/prettier */
import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { reset as resetForm, initialize } from 'redux-form';
import { BASE_API, USER_TOKEN } from 'config/consts';
import { uploadVoucherCodes } from '../../sales/productOrders/productOrdersActions';
import {
  VENDAS_ONLINE_PARCEIRO_FETCHED,
  VENDAS_ONLINE_PARCEIRO_DETAIL_FETCHED,
  VENDAS_ONLINE_PARCEIRO_LOADING,
  VENDAS_ONLINE_PARCEIRO_RESET,
  VENDAS_ONLINE_PARCEIRO_PARTNERS_FETCHED,
  VENDAS_ONLINE_PARCEIRO_CATEGORIES_FETCHED,
  VENDAS_ONLINE_PARCEIRO_GROUPINGS_FETCHED,
  VENDAS_ONLINE_PARCEIRO_STATIONS_FETCHED,
  VENDAS_ONLINE_PARCEIRO_TYPE_ID,
} from './types';

const getToken = () => JSON.parse(localStorage.getItem(USER_TOKEN));

// Envia os códigos de voucher pendentes (selecionados na aba Produto antes de o produto ter um ID)
// para os produtos já salvos no backend, vinculando pela ordem em que foram enviados (campo `order`).
const uploadPendingVoucherCodes = async (dispatch, actionId, pendingVoucherFiles) => {
  const pendingEntries = Object.entries(pendingVoucherFiles || {}).filter(
    ([, fileData]) => fileData?.codes?.length > 0
  );

  if (!actionId || pendingEntries.length === 0) return;

  try {
    // Busca a atividade recém salva para obter os IDs reais dos produtos
    const detailResponse = await axios.get(`${BASE_API}/actions/${actionId}`, {
      headers: { Authorization: getToken() },
    });

    let savedProducts = detailResponse.data?.products || [];
    if (!Array.isArray(savedProducts) || savedProducts.length === 0) {
      const rawConfig = detailResponse.data?.station_specific_data || detailResponse.data?.body;
      if (rawConfig) {
        const parsedBody = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
        savedProducts = parsedBody.products || [];
      }
    }

    const uploadPromises = pendingEntries.map(([index, fileData]) => {
      const savedProduct =
        savedProducts.find((p) => p.order === Number(index)) || savedProducts[Number(index)];

      if (!savedProduct?.id) {
        console.error(`Não foi possível localizar o produto salvo para o índice ${index}.`);
        return null;
      }

      return dispatch(
        uploadVoucherCodes({
          action_id: actionId,
          product_id: savedProduct.id,
          codes: fileData.codes,
        })
      ).catch((err) => {
        console.error(`Erro ao enviar vouchers do produto (índice ${index}):`, err);
      });
    });

    await Promise.all(uploadPromises.filter(Boolean));
  } catch (error) {
    console.error('Erro ao buscar produtos salvos para envio de vouchers:', error);
  }
};

// Buscar lista de atividades Vendas Online com Parceiro
export const getList = (stationId, page = 1, limit = 10) => {
  return async (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: true });
    try {
      const response = await axios.get(
        `${BASE_API}/actions-list-table/?where[station_id]=${stationId}&where[type_id]=${VENDAS_ONLINE_PARCEIRO_TYPE_ID}&where[active]=1&where[is_draft]=0`,
        {
          headers: { Authorization: getToken() },
        }
      );
      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_FETCHED,
        payload: {
          list: response.data.data || response.data,
          totalCount: response.data.total || response.data.length,
        },
      });
    } catch (error) {
      console.error('Erro ao buscar atividades:', error);
      toastr.error('Erro', 'Erro ao carregar atividades');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
    }
  };
};

// Buscar detalhes de uma atividade
export const getDetail = (actionId) => {
  return async (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: true });
    try {
      const response = await axios.get(`${BASE_API}/actions/${actionId}`, {
        headers: { Authorization: getToken() },
      });
      
      const data = response.data;
      let parsedBody = {};
      const rawConfig = data.station_specific_data || data.body;
      if (rawConfig) {
        try {
          parsedBody = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
        } catch (e) {
          console.warn('Erro ao parsear station_specific_data:', e);
        }
      }

      // Os produtos precisam vir do banco (data.products) para carregar o `id` real.
      // O JSON salvo em station_specific_data (parsedBody.products) pode estar
      // desatualizado ou nunca ter tido `id` — usá-lo como fonte do id faz o backend
      // achar que os produtos existentes foram removidos e apagar seus vouchers/códigos
      // ao salvar novamente. Aqui usamos data.products como base e só aproveitamos do
      // JSON campos extras que não existem na tabela Product (ex: has_voucher_import),
      // casando pela posição (mesma ordenação usada ao salvar/consultar).
      const dbProducts = Array.isArray(data.products) ? data.products : [];
      const bodyProducts = Array.isArray(parsedBody.products) ? parsedBody.products : [];
      const mergedProducts =
        dbProducts.length > 0
          ? dbProducts.map((dbProduct, index) => {
              const bodyMatch = bodyProducts[index] || {};
              return {
                ...bodyMatch,
                id: dbProduct.id,
                name: dbProduct.name,
                description: dbProduct.description,
                price: dbProduct.value,
                discount: dbProduct.discount_value,
                stock: dbProduct.stock,
                limit_per_user: dbProduct.limit,
                image: dbProduct.image,
                order: dbProduct.order,
              };
            })
          : bodyProducts;

      const formData = {
        id: data.id,
        ...parsedBody,
        ...data,
        body_name: data.name || parsedBody.name,
        body_description: data.description || parsedBody.description,
        body_summary: data.summary || parsedBody.summary,
        categories_field: data.categories || parsedBody.categories || [],
        groupings_field: data.groupings || parsedBody.groupings || [],
        stations_field: parsedBody.stations_field || [],
        partners_split: parsedBody.partners_split || [],
        products: mergedProducts,
      };

      dispatch({ type: VENDAS_ONLINE_PARCEIRO_DETAIL_FETCHED, payload: formData });
      dispatch(initialize('vendasOnlineParceiroForm', formData));
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
      toastr.error('Erro', 'Erro ao carregar detalhes da atividade');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
    }
  };
};

// Buscar parceiros disponíveis usando o mesmo endpoint do Link de Pagamento
export const getPartners = (stationIds) => {
  return async (dispatch) => {
    try {
      const ids = Array.isArray(stationIds) ? stationIds : [stationIds];
      
      const responses = await Promise.all(
        ids.map((stationId) =>
          axios
            .get(`${BASE_API}/gateway-digital-accounts/payment-link-partners/${stationId}`, {
              headers: { Authorization: getToken() },
            })
            .catch((e) => {
              console.error(`Erro ao buscar parceiros da station ${stationId}:`, e);
              return { data: [] };
            })
        )
      );

      // Consolidar parceiros sem duplicatas (usando user_id + establishment_id como chave única)
      const allPartners = [];
      const partnerKeys = new Set();
      responses.forEach((response) => {
        const data = response.data || [];
        (Array.isArray(data) ? data : []).forEach((partner) => {
          const key = `${partner.user_id}-${partner.establishment_id}`;
          if (partner && partner.user_id && !partnerKeys.has(key)) {
            partnerKeys.add(key);
            // Adaptar estrutura para compatibilidade com o componente
            allPartners.push({
              id: partner.user_id,
              user_id: partner.user_id,
              establishment_id: partner.establishment_id,
              name: partner.user_name || partner.name,
              email: partner.user_email || partner.email,
              cpf_cnpj: partner.cnpj || partner.cpf_cnpj || '', // O endpoint retorna 'cnpj'
              establishment_name: partner.establishment_name,
              social_name: partner.social_name,
              accounts: partner.accounts || [],
              hasDigitalAccount: partner.accounts && partner.accounts.length > 0,
            });
          }
        });
      });

      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_PARTNERS_FETCHED,
        payload: allPartners,
      });
    } catch (error) {
      console.error('Erro ao buscar parceiros:', error);
      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_PARTNERS_FETCHED,
        payload: [],
      });
    }
  };
};

// Salvar atividade (criar ou editar)
export const saveAction = (values, router, isEdit = false) => {
  return async (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: true });
    try {
      const payload = {
        ...values,
        type_id: VENDAS_ONLINE_PARCEIRO_TYPE_ID,
        name: values.body_name,
        description: values.body_description,
        summary: values.body_summary,
        body: JSON.stringify({
          ...values,
          name: values.body_name,
          description: values.body_description,
          summary: values.body_summary,
          partners_split: values.partners_split || [],
          products: values.products || [],
          stations_field: values.stations_field || [],
          station_images: values.station_images || {},
          recurrence_type: values.recurrence_type,
          payment_option: values.payment_option,
          allow_installments: values.allow_installments,
          installments_count: values.installments_count,
          installments_free: values.installments_free,
        }),
      };

      let response;
      if (isEdit && values.id) {
        response = await axios.put(`${BASE_API}/actions/${values.id}`, payload, {
          headers: { Authorization: getToken() },
        });
        toastr.success('Sucesso', 'Atividade atualizada com sucesso!');
      } else {
        response = await axios.post(`${BASE_API}/actions`, payload, {
          headers: { Authorization: getToken() },
        });
        toastr.success('Sucesso', 'Atividade criada com sucesso!');
      }

      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
      dispatch(resetForm('vendasOnlineParceiroForm'));
      
      if (router) {
        router.push('/owner/acoes/vendas-online-parceiro');
      }

      return response.data;
    } catch (error) {
      console.error('Erro ao salvar atividade:', error);
      toastr.error('Erro', error.response?.data?.message || 'Erro ao salvar atividade');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
      throw error;
    }
  };
};

// Salvar parcialmente (avançar etapa)
export const savePartially = (values, router, setValue, currentValue) => {
  return async (dispatch) => {
    try {
      // Apenas avança para próxima etapa sem salvar no backend
      setValue(currentValue + 1);
    } catch (error) {
      console.error('Erro ao salvar parcialmente:', error);
    }
  };
};

// Publicar atividade
export const publishAction = (actionId, stationIds) => {
  return async (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: true });
    try {
      await axios.post(
        `${BASE_API}/actions/${actionId}/publish`,
        { station_ids: stationIds },
        {
          headers: { Authorization: getToken() },
        }
      );
      toastr.success('Sucesso', 'Atividade publicada com sucesso!');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
    } catch (error) {
      console.error('Erro ao publicar:', error);
      toastr.error('Erro', 'Erro ao publicar atividade');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
    }
  };
};

// Encerrar atividade
export const endAction = (actionId) => {
  return async (dispatch) => {
    try {
      await axios.put(
        `${BASE_API}/actions/${actionId}/end`,
        {},
        {
          headers: { Authorization: getToken() },
        }
      );
      toastr.success('Sucesso', 'Atividade encerrada com sucesso!');
    } catch (error) {
      console.error('Erro ao encerrar:', error);
      toastr.error('Erro', 'Erro ao encerrar atividade');
    }
  };
};

// Reset do estado
export const resetState = () => {
  return (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_RESET });
    dispatch(resetForm('vendasOnlineParceiroForm'));
  };
};

// Dispatch form reset
export const dispatchFormReset = () => {
  return (dispatch) => {
    dispatch(resetForm('vendasOnlineParceiroForm'));
  };
};

// Buscar categorias
export const getCategories = (stationId) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(
        `${BASE_API}/categories?where[station_id]=${stationId}`,
        {
          headers: { Authorization: getToken() },
        }
      );
      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_CATEGORIES_FETCHED,
        payload: response.data || [],
      });
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    }
  };
};

// Buscar agrupamentos
export const getGroupings = (stationId) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(
        `${BASE_API}/groupings-list-table?where[active]=1&where[station_id]=${stationId}`,
        {
          headers: { Authorization: getToken() },
        }
      );
      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_GROUPINGS_FETCHED,
        payload: response.data || [],
      });
    } catch (error) {
      console.error('Erro ao buscar agrupamentos:', error);
    }
  };
};

// Buscar stations do usuário
export const getStations = () => {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${BASE_API}/stations`, {
        headers: { Authorization: getToken() },
      });
      dispatch({
        type: VENDAS_ONLINE_PARCEIRO_STATIONS_FETCHED,
        payload: response.data || [],
      });
    } catch (error) {
      console.error('Erro ao buscar stations:', error);
    }
  };
};

// Publicar atividade em múltiplas stations
export const publishToStations = (values, closeModal) => {
  return async (dispatch) => {
    dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: true });
    try {
      // Validar campos obrigatórios antes de enviar
      if (!values.body_name) {
        throw new Error('O título da atividade é obrigatório');
      }
      if (!values.body_description) {
        throw new Error('A descrição da atividade é obrigatória');
      }
      
      const stationIds = values.stations_field?.map((s) => s.id).filter(Boolean) || [];
      const primaryStationId = values.station_id;
      let targetStations = stationIds.length > 0 ? stationIds : [primaryStationId];
      targetStations = targetStations.filter(Boolean);
    
      if (targetStations.length === 0) {
        throw new Error('Nenhuma station válida selecionada para publicação');
      }
      
      let mainImage = values.image;
      if (!mainImage && values.station_images) {
        const stationImageValues = Object.values(values.station_images);
        if (stationImageValues.length > 0) {
          mainImage = stationImageValues[0];
        }
      }
      if (!mainImage && values.products?.length > 0) {
        const productWithImage = values.products.find(p => p.image);
        if (productWithImage) {
          mainImage = productWithImage.image;
        }
      }

      const cleanedPartnersSplit = (values.partners_split || []).map(p => ({
        user_id: p.user_id,
        establishment_id: p.establishment_id,
        name: p.name,
        email: p.email,
        cnpj: p.cnpj,
        percentage: p.percentage,
        recipient_id: p.recipient_id,
        account_id: p.account_id,
        hasDigitalAccount: p.hasDigitalAccount,
      }));
      const cleanedProducts = (values.products || []).map((p, index) => ({
        // Preserva o id do produto já existente para que o backend faça update
        // em vez de excluir/recriar (isso apagaria os vouchers/códigos já vinculados)
        ...(p.id ? { id: p.id } : {}),
        name: p.name,
        description: p.description,
        value: parseFloat(p.price || p.value) || 0,
        discount_value: parseFloat(p.discount || p.discount_value) || 0,
        stock: p.stock !== null && p.stock !== undefined ? parseInt(p.stock) : null,
        // limit_per_user null = campo esvaziado (ilimitado); só cai no `limit` legado
        // quando o form nem chegou a ter a chave, senão o valor antigo volta ao salvar
        limit:
          p.limit_per_user !== undefined ? parseInt(p.limit_per_user) || null : p.limit || null,
        image: p.image,
        order: index,
      }));

      // Preparar dados da atividade
      const actionData = {
        type_id: VENDAS_ONLINE_PARCEIRO_TYPE_ID,
        type_action: VENDAS_ONLINE_PARCEIRO_TYPE_ID,
        name: values.body_name,
        description: values.body_description,
        summary: values.body_summary,
        image: mainImage,
        start: values.start,
        end: values.end,
        participationStart: values.participationStart,
        participationEnd: values.participationEnd,
        active: 1,
        is_draft: 0,
        rules: values.rules,
        terms: values.terms,
        categories: values.categories_field?.map((c) => c.id) || [],
        groupings: values.groupings_field?.map((g) => g.id) || [],
        points: 0,
        limit: 0,
        sponsored: false,
        body: JSON.stringify({
          name: values.body_name,
          description: values.body_description,
          summary: values.body_summary,
          partners_split: cleanedPartnersSplit,
          products: cleanedProducts,
          stations_field: (values.stations_field || []).map(s => ({ id: s.id, name: s.name })),
          station_images: values.station_images || {},
          recurrence_type: values.recurrence_type || 'single',
          payment_option: values.payment_option || 'credit_and_payment',
          payment_methods: values.payment_methods || ['pix', 'credit_card', 'boleto'],
          allow_installments: values.allow_installments || false,
          installments_count: values.installments_count || 12,
          installments_free: values.installments_free || 0,
          use_vouchers: values.use_vouchers || false,
          use_own_codes: values.use_own_codes || false,
          date_validity_voucher: values.date_validity_voucher || null,
          single_product: values.single_product || false,
          has_partner: values.has_partner || false,
        }),
      };

      // Preparar target de segmentação
      let target = null;
      if (values.has_target) {
        if (values.segmentation_type === 'public') {
          target = {
            age: {
              from: values.from,
              to: values.to,
            },
            states: values.states || [],
            associated: values.associated || 'all',
            internal: values.internal || 'all',
            plans: values.segment_plans,
            entities: values.entities,
          };
        } else if (values.segmentation_type === 'users') {
          target = {
            segment_users: Array.isArray(values.segment_users)
              ? values.segment_users.map(u => u.value || u)
              : values.segment_users,
          };
        }
      }

      // Preparar dados para o novo endpoint
      const publishData = {
        name: values.body_name,
        description: values.body_description,
        summary: values.body_summary,
        categories: values.categories_field?.map((c) => c.id) || [],
        groupings: [],
        stations: targetStations,
        parent_station_id: primaryStationId || targetStations[0],
        station_images: values.station_images || {},
        start: values.start,
        end: values.end,
        participationStart: values.participationStart,
        participationEnd: values.participationEnd,
        rules: values.rules,
        terms: values.terms,
        image: mainImage,
        body: actionData.body,
        // Campos de segmentação
        has_target: values.has_target || false,
        target: target,
        audience_types: values.audience_types,
        segmentation_type: values.segmentation_type,
        from: values.from,
        to: values.to,
        states: values.states || [],
        segment_plans: values.segment_plans,
        segment_users: values.segment_users,
        // Campos de visibilidade
        only_public: values.only_public || false,
        featuredPrivate: values.featuredPrivate || false,
        featuredPublic: values.featuredPublic || false,
        // comments_and_likes_enabled deve ser false por padrão
        comments_and_likes_enabled: values.comments_and_likes_enabled === true,
        hasPriority: values.hasPriority || false,
        priority: values.priority,
        hasPriorityInCarousel: values.hasPriorityInCarousel || false,
        priorityCarousel: values.priorityCarousel,
        // Campos de limite
        limit: values.limit || 0,
        limitPerUser: values.limitPerUser || null,
        click_me_label: 'Comprar', // Padrão para type_id 22
        // Campos específicos
        partners_split: cleanedPartnersSplit,
        products: cleanedProducts,
        payment_methods: values.payment_methods || ['pix', 'credit_card', 'boleto'],
        payment_option: values.payment_option || 'credit_and_payment',
        recurrence_type: values.recurrence_type || 'single',
        allow_installments: values.allow_installments || false,
        installments_count: values.installments_count || 12,
        installments_free: values.installments_free || 0,
        use_vouchers: values.use_vouchers || false,
        use_own_codes: values.use_own_codes || false,
        // O input datetime-local não tem fuso; converte aqui (fuso do navegador) para
        // o servidor não interpretar o horário como UTC
        date_validity_voucher:
          (values.use_vouchers || values.use_own_codes) && values.date_validity_voucher
            ? new Date(values.date_validity_voucher).toISOString()
            : null,
        single_product: values.single_product || false,
        has_partner: values.has_partner || false,
      };
      let response;
      if (values.id) {
        response = await axios.put(
          `${BASE_API}/vendas-online-parceiro/update/${values.id}`,
          publishData,
          { headers: { Authorization: getToken() } }
        );
        toastr.success('Sucesso', 'Atividade atualizada com sucesso!');
      } else {
        response = await axios.post(
          `${BASE_API}/vendas-online-parceiro/publish`,
          publishData,
          { headers: { Authorization: getToken() } }
        );
        toastr.success('Sucesso', `Atividade publicada em ${targetStations.length} station(s)!`);
      }

      // Envia os códigos de voucher pendentes (produtos com códigos próprios selecionados
      // antes de existir ID). Na criação (POST /vendas-online-parceiro/publish), o id fica
      // aninhado em response.data.data.created_actions (uma atividade por station escolhida).
      const createdActionIds = values.id
        ? [values.id]
        : (response.data?.data?.created_actions || [])
            .map((createdAction) => createdAction.id)
            .filter(Boolean);

      await Promise.all(
        createdActionIds.map((createdActionId) =>
          uploadPendingVoucherCodes(dispatch, createdActionId, values.pending_voucher_files)
        )
      );

      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
      dispatch(resetForm('vendasOnlineParceiroForm'));

      if (closeModal) {
        closeModal();
      }
      window.location.href = '#/owner/acoes';
    } catch (error) {
      console.error('Erro ao publicar:', error);
      toastr.error('Erro', error.response?.data?.message || 'Erro ao publicar atividade');
      dispatch({ type: VENDAS_ONLINE_PARCEIRO_LOADING, payload: false });
      throw error;
    }
  };
};

// Gerar link de pagamento para produto
export const generatePaymentLink = (actionId, productId, userId, amount, partnersplit) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${BASE_API}/gateway/payment-links`,
        {
          action_id: actionId,
          product_id: productId,
          user_id: userId,
          amount: amount,
          partners_split: partnersplit,
          type: 'vendas_online_parceiro',
        },
        {
          headers: { Authorization: getToken() },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Erro ao gerar link de pagamento:', error);
      toastr.error('Erro', 'Erro ao gerar link de pagamento');
      throw error;
    }
  };
};

// Processar pagamento
export const processPayment = (paymentData) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${BASE_API}/gateway/process-payment`,
        paymentData,
        {
          headers: { Authorization: getToken() },
        }
      );

      if (response.data.success) {
        toastr.success('Sucesso', 'Pagamento processado com sucesso!');
      }

      return response.data;
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      toastr.error('Erro', error.response?.data?.message || 'Erro ao processar pagamento');
      throw error;
    }
  };
};
